import {
  Hct,
  SchemeContent,
  SchemeExpressive,
  SchemeFidelity,
  SchemeFruitSalad,
  SchemeMonochrome,
  SchemeNeutral,
  SchemeRainbow,
  SchemeTonalSpot,
  SchemeVibrant,
  TonalPalette,
  argbFromHex,
  hexFromArgb,
} from "@material/material-color-utilities";

import { SelectedTheme, TypeTheme } from "@revolt/state/stores/Theme";

/**
 * Generate the Material variables from the given properties
 *
 * Currently only generates color keys
 */
export function createMaterialColourVariables<P extends string>(
  theme: SelectedTheme,
  prefix: P,
): addPrefixToObject<MaterialColours, P> {
  switch (theme.preset) {
    case "you":
      return Object.entries(
        generateMaterialYouScheme(
          theme.accent,
          theme.darkMode,
          theme.contrast,
          theme.variant,
          theme.surface,
          theme.surfaceTint,
          theme.textColour,
        ),
      ).reduce(
        (d, [key, value]) => ({
          ...d,
          [`${prefix}${key}`]: value,
        }),
        {} as addPrefixToObject<MaterialColours, P>,
      );
    default:
      return {} as never;
  }
}

/**
 * Create R,G,B triplets for MDUI variables
 */
export function createMduiColourTriplets<P extends string>(
  theme: SelectedTheme,
  prefix: P,
): addPrefixToObject<MaterialColours, P> {
  const variables = createMaterialColourVariables(theme, prefix);

  for (const key in variables) {
    const [_, r, g, b] = /#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})/i.exec(
      variables[key as keyof typeof variables] as string,
    )!;

    variables[key as keyof typeof variables] =
      `${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}` as never;
  }

  return variables;
}

type addPrefixToObject<T, P extends string> = {
  [K in keyof T as K extends string ? `${P}${K}` : never]: T[K];
};

type _addSuffixToObject<T, S extends string> = {
  [K in keyof T as K extends string ? `${K}${S}` : never]: T[K];
};

type MaterialColours = {
  primary: string;
  "on-primary": string;
  "primary-container": string;
  "on-primary-container": string;
  secondary: string;
  "on-secondary": string;
  "secondary-container": string;
  "on-secondary-container": string;
  tertiary: string;
  "on-tertiary": string;
  "tertiary-container": string;
  "on-tertiary-container": string;
  error: string;
  "on-error": string;
  "error-container": string;
  "on-error-container": string;

  "primary-fixed": string;
  "primary-fixed-dim": string;
  "on-primary-fixed": string;
  "on-primary-fixed-variant": string;
  "secondary-fixed": string;
  "secondary-fixed-dim": string;
  "on-secondary-fixed": string;
  "on-secondary-fixed-variant": string;
  "tertiary-fixed": string;
  "tertiary-fixed-dim": string;
  "on-tertiary-fixed": string;
  "on-tertiary-fixed-variant": string;

  "surface-dim": string;
  surface: string;
  "surface-bright": string;

  "surface-container-lowest": string;
  "surface-container-low": string;
  "surface-container": string;
  "surface-container-high": string;
  "surface-container-highest": string;

  "on-surface": string;
  "on-surface-variant": string;
  outline: string;
  "outline-variant": string;

  "inverse-surface": string;
  "inverse-on-surface": string;
  "inverse-primary": string;

  scrim: string;
  shadow: string;
};

type SurfaceKey =
  | "surface-dim"
  | "surface"
  | "surface-bright"
  | "surface-container-lowest"
  | "surface-container-low"
  | "surface-container"
  | "surface-container-high"
  | "surface-container-highest";

/**
 * Konkord: tones used for the surfaces in dark mode when not using the
 * generated ones (Material defaults are 4 to 24)
 */
const SURFACE_TONES: Record<
  TypeTheme["m3Surface"],
  Record<SurfaceKey, number>
> = {
  // Konkord's look: everything on pure black, the columns split by hairlines
  // (like Discord); cards, menus and hovers in near black greys barely tinted
  // with the accent
  default: {
    "surface-container-lowest": 0,
    "surface-dim": 0,
    surface: 0,
    "surface-container-low": 0,
    "surface-container": 6,
    "surface-container-high": 10.5,
    "surface-container-highest": 15,
    "surface-bright": 20,
  },
  // Stellar: deep night blue with the accent's hue
  space: {
    "surface-container-lowest": 3,
    "surface-dim": 3,
    surface: 5,
    "surface-container-low": 5,
    "surface-container": 9,
    "surface-container-high": 13,
    "surface-container-highest": 18,
    "surface-bright": 22,
  },
  // OLED: background is pure black, containers barely lifted
  black: {
    "surface-container-lowest": 0,
    "surface-dim": 0,
    surface: 0,
    "surface-container-low": 3,
    "surface-container": 5,
    "surface-container-high": 8,
    "surface-container-highest": 12,
    "surface-bright": 16,
  },
  // lighter greys (Discord-like dark)
  grey: {
    "surface-container-lowest": 12,
    "surface-dim": 14,
    surface: 14,
    "surface-container-low": 17,
    "surface-container": 19,
    "surface-container-high": 23,
    "surface-container-highest": 28,
    "surface-bright": 30,
  },
};

/**
 * Konkord: hue of the night blue background (Stellar)
 */
const NIGHT_BLUE_HUE = Hct.fromInt(argbFromHex("#3b4cca")).hue;

/**
 * Konkord: dark mode tones with a chosen background colour
 */
const TINTED_TONES: Record<SurfaceKey, number> = {
  "surface-container-lowest": 6,
  "surface-dim": 6,
  surface: 8,
  "surface-container-low": 8,
  "surface-container": 12,
  "surface-container-high": 16,
  "surface-container-highest": 21,
  "surface-bright": 25,
};

/**
 * Konkord: Material's light mode surface tones (used with a chosen
 * background colour)
 */
const LIGHT_TONES: Record<SurfaceKey, number> = {
  "surface-container-lowest": 100,
  "surface-dim": 87,
  surface: 98,
  "surface-container-low": 96,
  "surface-container": 94,
  "surface-container-high": 92,
  "surface-container-highest": 90,
  "surface-bright": 98,
};

/**
 * Konkord: mix two "#rrggbb" colours
 * @param a First colour
 * @param b Second colour
 * @param amount How much of b (0-1)
 * @returns Mixed colour
 */
function mixHex(a: string, b: string, amount: number) {
  const channels = (hex: string) =>
    [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const from = channels(a);
  const to = channels(b);
  return (
    "#" +
    from
      .map((c, i) =>
        Math.round(c + (to[i] - c) * amount)
          .toString(16)
          .padStart(2, "0"),
      )
      .join("")
  );
}

/**
 * Generate a Material You colour scheme
 * @param accent Accent colour in hex format
 * @param darkMode Dark mode
 * @param constrat Constrast level
 * @param variant Scheme variant
 * @param surface Konkord: dark mode surface override
 * @param tint Konkord: colour the backgrounds take their hue from
 * @param text Konkord: text colour
 * @returns Material colours
 */
export function generateMaterialYouScheme(
  accent: string,
  darkMode: boolean,
  contrast: number,
  variant: TypeTheme["m3Variant"],
  surface: TypeTheme["m3Surface"] = "default",
  tint: string | null = null,
  text: string | null = null,
): MaterialColours {
  const hct = Hct.fromInt(argbFromHex(accent));

  let scheme;
  switch (variant) {
    case "content":
      scheme = new SchemeContent(hct, darkMode, contrast);
      break;
    case "expressive":
      scheme = new SchemeExpressive(hct, darkMode, contrast);
      break;
    case "fidelity":
      scheme = new SchemeFidelity(hct, darkMode, contrast);
      break;
    case "fruit_salad":
      scheme = new SchemeFruitSalad(hct, darkMode, contrast);
      break;
    case "monochrome":
      scheme = new SchemeMonochrome(hct, darkMode, contrast);
      break;
    case "neutral":
      scheme = new SchemeNeutral(hct, darkMode, contrast);
      break;
    case "rainbow":
      scheme = new SchemeRainbow(hct, darkMode, contrast);
      break;
    case "vibrant":
      scheme = new SchemeVibrant(hct, darkMode, contrast);
      break;
    case "tonal_spot":
    default:
      scheme = new SchemeTonalSpot(hct, darkMode, contrast);
      break;
  }

  const colours: MaterialColours = {
    primary: hexFromArgb(scheme.primary),
    "on-primary": hexFromArgb(scheme.onPrimary),
    "primary-container": hexFromArgb(scheme.primaryContainer),
    "on-primary-container": hexFromArgb(scheme.onPrimaryContainer),
    secondary: hexFromArgb(scheme.secondary),
    "on-secondary": hexFromArgb(scheme.onSecondary),
    "secondary-container": hexFromArgb(scheme.secondaryContainer),
    "on-secondary-container": hexFromArgb(scheme.onSecondaryContainer),
    tertiary: hexFromArgb(scheme.tertiary),
    "on-tertiary": hexFromArgb(scheme.onTertiary),
    "tertiary-container": hexFromArgb(scheme.tertiaryContainer),
    "on-tertiary-container": hexFromArgb(scheme.onTertiaryContainer),
    error: hexFromArgb(scheme.error),
    "on-error": hexFromArgb(scheme.onError),
    "error-container": hexFromArgb(scheme.errorContainer),
    "on-error-container": hexFromArgb(scheme.onErrorContainer),

    "primary-fixed": hexFromArgb(scheme.primaryFixed),
    "primary-fixed-dim": hexFromArgb(scheme.primaryFixedDim),
    "on-primary-fixed": hexFromArgb(scheme.onPrimaryFixed),
    "on-primary-fixed-variant": hexFromArgb(scheme.onPrimaryFixedVariant),
    "secondary-fixed": hexFromArgb(scheme.secondaryFixed),
    "secondary-fixed-dim": hexFromArgb(scheme.onSecondaryFixed),
    "on-secondary-fixed": hexFromArgb(scheme.onSecondaryFixed),
    "on-secondary-fixed-variant": hexFromArgb(scheme.onSecondaryFixedVariant),
    "tertiary-fixed": hexFromArgb(scheme.tertiaryFixed),
    "tertiary-fixed-dim": hexFromArgb(scheme.tertiaryFixedDim),
    "on-tertiary-fixed": hexFromArgb(scheme.onTertiaryFixed),
    "on-tertiary-fixed-variant": hexFromArgb(scheme.onTertiaryFixedVariant),

    "surface-dim": hexFromArgb(scheme.surfaceDim),
    surface: hexFromArgb(scheme.surface),
    "surface-bright": hexFromArgb(scheme.surfaceBright),

    "surface-container-lowest": hexFromArgb(scheme.surfaceContainerLowest),
    "surface-container-low": hexFromArgb(scheme.surfaceContainerLow),
    "surface-container": hexFromArgb(scheme.surfaceContainer),
    "surface-container-high": hexFromArgb(scheme.surfaceContainerHigh),
    "surface-container-highest": hexFromArgb(scheme.surfaceContainerHighest),

    "on-surface": hexFromArgb(scheme.onSurface),
    "on-surface-variant": hexFromArgb(scheme.onSurfaceVariant),
    outline: hexFromArgb(scheme.outline),
    "outline-variant": hexFromArgb(scheme.outlineVariant),

    "inverse-surface": hexFromArgb(scheme.inverseSurface),
    "inverse-on-surface": hexFromArgb(scheme.inverseOnSurface),
    "inverse-primary": hexFromArgb(scheme.inversePrimary),

    scrim: hexFromArgb(scheme.scrim),
    shadow: hexFromArgb(scheme.shadow),
  };

  // Konkord: replace the dark mode surfaces, keeping the scheme's hue
  // (grey also drops most of its chroma so it reads as neutral grey)
  // (night blue is always blue; a chosen background colour gives its hue,
  // with its chroma capped so text stays readable)
  const tinted = tint ? Hct.fromInt(argbFromHex(tint)) : undefined;
  if (darkMode) {
    const neutral = scheme.neutralPalette;
    const hue =
      tinted?.hue ?? (surface === "space" ? NIGHT_BLUE_HUE : neutral.hue);
    const palette =
      surface === "grey"
        ? TonalPalette.fromHueAndChroma(
            hue,
            Math.min(tinted?.chroma ?? neutral.chroma, tinted ? 8 : 3),
          )
        : surface === "default"
          ? TonalPalette.fromHueAndChroma(
              hue,
              tinted ? Math.min(tinted.chroma, 24) : 6,
            )
          : surface === "space"
            ? TonalPalette.fromHueAndChroma(
                hue,
                tinted ? Math.min(tinted.chroma, 24) : 18,
              )
            : tinted
              ? TonalPalette.fromHueAndChroma(hue, Math.min(tinted.chroma, 10))
              : neutral;

    // a chosen background colour must show: lift the black surfaces to dark
    // tones of it (OLED keeps its black)
    const tones =
      tinted && surface !== "black" ? TINTED_TONES : SURFACE_TONES[surface];
    for (const key of Object.keys(tones) as SurfaceKey[]) {
      colours[key] = hexFromArgb(palette.tone(tones[key]));
    }
  }

  // Konkord: light mode backgrounds in the chosen colour
  if (!darkMode && tinted) {
    const palette = TonalPalette.fromHueAndChroma(
      tinted.hue,
      Math.min(tinted.chroma, 16),
    );
    for (const key of Object.keys(LIGHT_TONES) as SurfaceKey[]) {
      colours[key] = hexFromArgb(palette.tone(LIGHT_TONES[key]));
    }
  }

  // Konkord's look: a vivid accent with white text (Material's dark mode uses
  // pastel tones) and neutral containers instead of accent tinted ones
  if (darkMode && (surface === "default" || surface === "space")) {
    const vivid = TonalPalette.fromHueAndChroma(
      hct.hue,
      Math.max(hct.chroma, 48),
    );
    colours.primary = hexFromArgb(vivid.tone(60));
    colours["on-primary"] = "#ffffff";
    colours["primary-container"] = hexFromArgb(vivid.tone(30));
    colours["on-primary-container"] = hexFromArgb(vivid.tone(92));

    const neutral = TonalPalette.fromHueAndChroma(scheme.neutralPalette.hue, 6);
    colours["secondary-container"] = hexFromArgb(neutral.tone(20));
    colours["on-secondary-container"] = hexFromArgb(neutral.tone(90));
  }

  // Konkord: chosen text colour; secondary text is the same colour faded
  // towards the background
  if (text) {
    colours["on-surface"] = text.slice(0, 7);
    colours["on-surface-variant"] = mixHex(
      text,
      colours["surface-container-lowest"],
      0.25,
    );
  }

  return colours;
}
